import i18next from "i18next";

/**
 * What a WireGuard peer needs before it can be sent, in one place.
 *
 * Shared rather than owned by the create wizard, because a peer is described
 * twice: once when a connection is made and again whenever the credential
 * behind it is rotated. The rules do not change between those, and two copies
 * of them drift — the create page would gain a check the settings panel did
 * not, and the difference would show up as a 400 from the API instead.
 */
export interface TunnelDraft {
  name: string;
  endpoint: string;
  peerPublicKey: string;
  privateKey: string;
  address: string;
  allowedIps: string;
}

/**
 * What is missing, as a sentence rather than a boolean.
 *
 * Returning the reason is what lets a disabled button say why instead of just
 * looking broken — the same shape the app wizard uses, and the reason this is
 * worth testing separately from the screens that render it.
 *
 * Grouped by the wizard's steps because that is where the grouping is load
 * bearing. The settings panel has no steps and asks for both at once.
 */
export function stepBlocker(step: number, draft: TunnelDraft): string | null {
  /* Read at call time rather than at import, as cron.ts does, so the words
     follow a language switch without the wizard remounting. */
  const t = i18next.getFixedT(null, "create");
  if (step === 0) {
    if (!draft.name.trim()) return t("tunnel.blocker.name");
    if (!draft.endpoint.trim()) return t("tunnel.blocker.endpoint");
    if (!draft.peerPublicKey.trim()) return t("tunnel.blocker.peerPublicKey");
    return null;
  }
  if (step === 1) {
    if (!draft.privateKey.trim()) return t("tunnel.blocker.privateKey");
    if (!draft.address.trim()) return t("tunnel.blocker.address");
    if (!draft.allowedIps.trim()) return t("tunnel.blocker.allowedIps");
    return null;
  }
  return null;
}
